/* eslint-disable @typescript-eslint/no-explicit-any */
import moment from 'moment'
import { SVGS } from '@/assets/svgs'
import {
  ConnectionModel,
  DMTopic,
  EgoBasicInfo,
  IPatternAssetTemplate,
  MyConnection,
  Note,
  PatternMeshColor,
  Topic,
  TopicStats,
} from '@/types'
import Resizer from 'react-image-file-resizer'
import { AVATAR_BG_COLORS, AVATAR_FILE_EXTENSIONS, NoteType, TopicType } from './constants'
import { Assets } from './pattern-assets'
import htmlToDraft from 'html-to-draftjs'
import { number } from 'prop-types'

export const Patterns = {
  email: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
  phone: /^\d{10}$/,
  // eslint-disable-next-line no-useless-escape
  password: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
  firstName: /^[a-zA-Z]+|[a-zA-Z]+ [a-zA-Z]+$/,
  lastName: /^[a-zA-Z]+$/,
}

export const getCopyRightText = () => {
  return `© ${new Date().getFullYear()} ALL RIGHTS RESERVED.`
}

export const validateEmail = (emailAdress: string) => {
  // eslint-disable-next-line no-useless-escape
  const regexEmail = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
  return emailAdress.match(regexEmail)
}

export const validatePhoneNumber = (number: string) => {
  // eslint-disable-next-line no-useless-escape
  const regexPhone = /^\d{10}$/
  return number.match(regexPhone)
}

export const validatePassword = (password: string) => {
  // eslint-disable-next-line no-useless-escape
  const regexPhone = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/
  return password.match(regexPhone)
}

export const validateFirstName = (name: string) => {
  // eslint-disable-next-line no-useless-escape
  const regexPhone = /^[a-zA-Z]+|[a-zA-Z]+ [a-zA-Z]+$/
  return name.match(regexPhone)
}

export const validateLastName = (name: string) => {
  // eslint-disable-next-line no-useless-escape
  const regexPhone = /^[a-zA-Z]+$/
  return name.match(regexPhone)
}

// iOS/Android report inconsistent MIME for HEIC (sometimes empty or
// application/octet-stream depending on browser/OS version) — check the
// extension too, not just file.type.
const isHeic = (file: File): boolean => {
  const type = file.type.toLowerCase()
  if (type === 'image/heic' || type === 'image/heif') return true
  return /\.hei[cf]$/i.test(file.name)
}

// Canvas-based resizing (react-image-file-resizer, below) can't decode HEIC
// outside Safari, so pre-convert to JPEG first. Dynamically imported — most
// uploads are already JPEG/PNG and never pay for this.
const decodeHeicToJpegFile = async (file: File): Promise<File> => {
  const { default: heic2any } = await import('heic2any')
  const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 })
  // heic2any returns an array for multi-image HEIC containers (Live Photo,
  // burst) — take the first frame, same as treating a photo as a single still.
  const out = Array.isArray(blob) ? blob[0] : blob
  return new File([out], file.name.replace(/\.hei[cf]$/i, '.jpg'), { type: 'image/jpeg' })
}

// Every upload path (album photos, profile picture, cover photo, rich text
// attachments) funnels through this one function — the HEIC pre-convert
// lives here, not in uploadMedia, so it covers direct callers too.
// `maxDim`/`quality` default to today's 1000px/q100 behavior — album uploads
// pass a higher `maxDim` when the owner's album + live tier both qualify for
// full-resolution uploads (see FEATURE_KEYS.FULL_RESOLUTION_UPLOAD).
export const resizeFile = async (file: File, maxDim = 1000, quality = 100) => {
  const source = isHeic(file) ? await decodeHeicToJpegFile(file) : file
  return new Promise((resolve) => {
    Resizer.imageFileResizer(
      source,
      maxDim,
      maxDim,
      'jpg',
      quality,
      0,
      (uri: any) => {
        resolve(uri)
      },
      'file',
    )
  })
}

export const uploadMedia = async function (data: File, maxDim?: number, quality?: number) {
  try {
    return await resizeFile(data, maxDim, quality)
  } catch (err) {
    return { isError: true, errorMessage: err }
  }
}

export const getFileUniqueKey = (file: File) => {
  return `${file.lastModified}-${file.size}-${file.name}`
}

export const getTooltipTitleForPrivacyUsers = (topic: Topic | DMTopic) => {
  if (topic.visibility === '4') {
    return topic.connections.map((i) => `${i.firstName} ${i.lastName}`).join(', ')
  }
  if (topic.visibility === '5') {
    return (topic as Topic).cliques?.map((i) => i.cliqueName).join(', ') || ''
  }
  if (topic.visibility === '6') {
    return `${topic.connections[0]?.firstName} ${topic.connections[0]?.lastName}`
  }
  return ''
}

export const convertToGMT = (dateString: string) => {
  return `${dateString} GMT`
}

export const isValidAvatarUrl = (url: string) => {
  if (url.includes('profile_male')) return false
  const lowerCase = url.toLowerCase()
  if (!AVATAR_FILE_EXTENSIONS.some((i) => lowerCase.includes(i))) return false
  try {
    new URL(url)
    return true
  } catch (err) {
    return false
  }
}

/**
 * Spectrum-family palette for "no real photo" avatar fallbacks — same 4
 * tone/ink pairs already used for comment author avatars (see CommentThread's
 * avatarBg/avatarInk). Kept as plain hex here (rather than importing
 * CommentAvatarTone) since callers like presence avatars just need a
 * background+text swatch, not the comment-specific tone name.
 */
const PRESENCE_AVATAR_TONES = [
  { bg: '#ffcf7d', ink: '#4a3510' },
  { bg: '#fea67d', ink: '#4a1e0a' },
  { bg: '#be9dfd', ink: '#2d1a54' },
  { bg: '#01dafe', ink: '#05323b' },
]

/** Deterministic per-user color pick from PRESENCE_AVATAR_TONES — same person always lands on the same tone, different people are visually distinct instead of all sharing one identical gradient. */
export const getPresenceAvatarTone = (id: string) => {
  let hash = 0
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0
  return PRESENCE_AVATAR_TONES[hash % PRESENCE_AVATAR_TONES.length]
}

export const SocialIcons = {
  like: {
    key: 'like',
    icon: SVGS.ActionLike,
    width: '15.27px',
    height: '14px',
  },
  likeFilled: {
    key: 'like-filled',
    icon: SVGS.ActionLikeFilled,
    width: '15.27px',
    height: '14px',
  },

  comment: {
    key: 'comment',
    icon: SVGS.ActionComment,
    width: '16.14px',
    height: '14.04px',
  },

  view: {
    key: 'visit',
    icon: SVGS.ActionVisit,
    width: '17.73px',
    height: '14px',
  },

  note: {
    key: 'media',
    icon: SVGS.ActionMedia,
    width: '14px',
    height: '14px',
  },
}

export const getTopicStates = (topic: Topic) => {
  const states = [
    {
      ...SocialIcons.like,
      value: Number(topic.totalLikes),
    },
    {
      ...SocialIcons.comment,
      value: Number(topic.totalComments),
    },
    {
      ...SocialIcons.view,
      value: Number(topic.visitorVisits) + Number(topic.ownerVisits),
    },
  ]

  let mediaCount = 0
  if (topic.topicType == TopicType.ALBUM.toString()) {
    // topic.notes?.forEach((note) => (mediaCount += note.egoList?.length || 0))
    mediaCount = Number(topic.noteCount) - 1
  } else {
    mediaCount = Number(topic.noteCount) - 1
  }

  states.splice(0, 0, {
    ...SocialIcons.note,
    value: mediaCount || Number(topic.photoCount),
  })
  states.push()
  return states.filter((i) => i.value > 0)
}

export const getDMTopicStates = (state: TopicStats) => {
  const states = [
    {
      key: 'like',
      icon: SVGS.ActionLike,
      value: Number(state.noteThumbsUp),
    },
    {
      key: 'comment',
      icon: SVGS.ActionComment,
      value: Number(state.noteComments),
    },
    {
      key: 'visit',
      icon: SVGS.ActionVisit,
      value: Number(state.visitorVisits) + Number(state.ownerVisits),
    },
    {
      visit: 'media',
      icon: SVGS.ActionMedia,
      value: Number(state.egoCount),
    },
  ]
  return states.filter((i) => i.value > 0)
}

export const getAvatarColorWithName = (name: string) => {
  const [firstName, lastName] = name.split(' ')
  const hash = firstName.charCodeAt(0) + (lastName || 'a').charCodeAt(0) + name.length
  return AVATAR_BG_COLORS[hash % AVATAR_BG_COLORS.length]
}

export const isPatternTopicType = (type: string | number) => {
  return type === '200' || type === 200
}

export const isGalleryTopicType = (type: string | number) => {
  return type === '201' || type === 201
}

export const isWallArtTopicType = (type: string | number) => {
  return type === '202' || type === 202
}

export const isQuiltTopicType = (type: string | number) => {
  return type === '203' || type === 203
}

export const isCustomPrintTopicType = (type: string | number) => {
  return type == TopicType.CUSTOM_PRINTS
}

export const isBabylonType = (type: string | number) => {
  return (
    isPatternTopicType(type) ||
    isQuiltTopicType(type) ||
    isGalleryTopicType(type) ||
    isWallArtTopicType(type) ||
    isCustomPrintTopicType(type)
  )
}

export const getFullImageURL = (imageURL: string) => {
  if (imageURL?.length > 0) {
    let result = imageURL?.replace('/photo.album.', '/photo.full.')
    result = result?.replace('/photo.thumb.', '/photo.full.')
    return result
  }
  return ''
}

export const getThumbImageURL = (imageURL: string) => {
  if (imageURL?.length > 0) {
    let result = imageURL?.replace('/photo.album.', '/photo.thumb.')
    result = result?.replace('/photo.full.', '/photo.thumb.')
    return result
  }
  return ''
}

export const getAssetThumb = (type: NoteType, id: number): string | undefined => {
  let result: string | undefined

  if (type == NoteType.TEMPLATE) {
    result = Assets.templates.find((e) => e.id == id)?.thumb
  } else if (type == NoteType.MATERIAL) {
    result = Assets.materials.find((e) => e.id == id)?.thumb
  } else if (type == NoteType.ACCENT) {
    result = Assets.accents.find((e) => e.id == id)?.thumb
  } else if (type == NoteType.FRAME) {
    result = Assets.frames.find((e) => e.id == id)?.thumb
  } else if (type == NoteType.PRINT) {
    result = Assets.prints.standard.find((e) => e.id == id)?.thumb
  } else if (type == NoteType.PATTERNFRAME) {
    result = Assets.patternFrames.find((e) => e.id == id)?.thumb
  }
  return result ? `/assets/patterns/${result}` : undefined
}

export const getAssetName = (type: NoteType, id: number): string | undefined => {
  let result: string | undefined

  if (type == NoteType.TEMPLATE) {
    result = Assets.templates.find((e) => e.id == id)?.name
  } else if (type == NoteType.MATERIAL) {
    result = Assets.materials.find((e) => e.id == id)?.name
  } else if (type == NoteType.ACCENT) {
    result = Assets.accents.find((e) => e.id == id)?.name
  } else if (type == NoteType.FRAME) {
    result = Assets.frames.find((e) => e.id == id)?.name
  } else if (type == NoteType.PRINT) {
    result = Assets.prints.standard.find((e) => e.id == id)?.name
  } else if (type == NoteType.PATTERNFRAME) {
    result = Assets.patternFrames.find((e) => e.id == id)?.name
  }

  return result
}

export const getPlainNoteTextFromNote = (note: Note) => {
  const { noteText = '' } = note || {}
  try {
    const parsed = JSON.parse(noteText)
    const draft = htmlToDraft(parsed.text)
    const value = draft.contentBlocks.map((block) => block.getText()).join('\n')
    return value
  } catch (error) {
    return noteText
  }
}

export const getNowInUTC = () => {
  return moment().utc().format('yyyy-MM-DD HH:mm:ss')
}

export const scrollToBottom = (scrollRef: any, delay: number = 0) => {
  setTimeout(() => {
    const scrollBox = scrollRef.current
    if (scrollBox) {
      scrollBox.scrollTop = scrollBox?.scrollHeight
      console.log('Scrolling down')
    }
  }, delay)
}

export const convertMyConnectionToConnectionModel = (input: MyConnection) => {
  const model: ConnectionModel = {
    ...input,
    isAssigned: false,
    isOwner: false,
    profilePicFullURL: input.albumImgURL,
    profilePicThumbURL: input.thumbImgURL,
    profilePicURL: input.albumImgURL,
    searchType: 'user',
    userName: '',
  }
  return model
}

export const convertEgoBasicInfoToConnectionModel = (input: EgoBasicInfo) => {
  const model: ConnectionModel = {
    ...input,
    isAssigned: false,
    isOwner: false,
    profilePicFullURL: input.profilePicFullURL,
    profilePicThumbURL: input.profilePicThumbURL,
    profilePicURL: input.profileCoverURL,
    searchType: 'user',
    reqReceived: input.reqSent ? `${input.reqReceivedConnReqID}` : false,
  }
  return model
}

export const isUniqueColor = (
  swatches: PatternMeshColor[][],
  color: PatternMeshColor[],
): boolean => {
  let result = true

  for (let i = 0; i < swatches.length; i++) {
    let aSwatch = swatches[i]
    let subItemFoundCount = 0
    for (let j = 0; j < aSwatch.length; j++) {
      if (
        aSwatch[j]?.r === color[j]?.r &&
        aSwatch[j]?.g === color[j]?.g &&
        aSwatch[j]?.b === color[j]?.b
      ) {
        subItemFoundCount++
      }
    }

    if (subItemFoundCount === aSwatch.length) {
      result = false
      break
    }
  }

  return result
}

export const isNumeric = (value: any): boolean => {
  return !isNaN(value as number) && !isNaN(parseFloat(value))
}
